import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class ContextUserService {
  private readonly LS_ACTIVE_PROFILE_ID = 'activeProfileId';
  private readonly LS_PROFILE_IDS = 'profileIds';

  private profileIds$ = new BehaviorSubject<number[]>([]);
  private activeProfileId$ = new BehaviorSubject<number | null>(null);

  private context: Record<string, any> = {};
  private contextSubjects: Record<string, BehaviorSubject<any>> = {};
  private readonly triggerFrequency = 10;
  private readonly nameUserLocalStorage: string = 'userInternoApp';
  private callCount = 0;


  constructor(private readonly _userService: UserService) {
    const storedProfileIds = this.safeParse(
      localStorage.getItem(this.LS_PROFILE_IDS)
    );
    if (Array.isArray(storedProfileIds)) {
      this.profileIds$.next(storedProfileIds);
    }

    const storedActiveId = this.safeParse(
      localStorage.getItem(this.LS_ACTIVE_PROFILE_ID)
    );
    if (typeof storedActiveId === 'number') {
      this.activeProfileId$.next(storedActiveId);
    }

  }

  /** Setea valor en contexto y notifica a suscriptores */
  set(key: string, value: any): void {
    this.context[key] = value;

    if (!this.contextSubjects[key]) {
      this.contextSubjects[key] = new BehaviorSubject(value);
    } else {
      this.contextSubjects[key].next(value);
    }
  }

  /** Obtiene un valor del contexto */
  get(key: string): any {
    return this.context[key];
  }

  /**
   * Obtiene usuario desde el contexto o desde localStorage.
   */
  get getUser(): any {
    const contextUser = this.context["user"];
    const storedUserCierre = this.safeParse(localStorage.getItem(this.nameUserLocalStorage));
    const storedCurrentUser = this.safeParse(localStorage.getItem("currentUser"));

    return contextUser || storedUserCierre || storedCurrentUser;
  }

  /** Limpia un valor del contexto */
  clear(key: string): void {
    delete this.context[key];
    this.contextSubjects[key]?.next(null);
  }

  /** Observa un valor del contexto en tiempo real */
  watch(key: string): Observable<any> {
    if (!this.contextSubjects[key]) {
      this.contextSubjects[key] = new BehaviorSubject(null);
    }
    return this.contextSubjects[key].asObservable();
  }

  /**
   * @description
   * Guarda el usuario en el contexto, pero solo hace petición si corresponde.
   */
  async setContextUser(): Promise<void> {
    if (!this.setCounter()) return;

    const storedUser = this.safeParse(localStorage.getItem("currentUser"));
    if (!storedUser?.email) return;

    const email = storedUser.email;
    this.set("userEmail", email);

    try {
      const res = await firstValueFrom(this._userService.getByEmail(email));
      if (res?.data) {
        localStorage.setItem(this.nameUserLocalStorage, JSON.stringify(res.data));
        this.set("user", res.data);

        // extraer solo prId[]
        const profiles = res.data.profiles ?? [];
        const profileIds = profiles.map((p: any) => p.prId);

        // emitir y persistir ids
        this.profileIds$.next(profileIds);
        localStorage.setItem(this.LS_PROFILE_IDS, JSON.stringify(profileIds));

        // definir perfil activo inicial
        const storedActiveId = this.safeParse(
          localStorage.getItem(this.LS_ACTIVE_PROFILE_ID)
        );

        const validStored =
          typeof storedActiveId === 'number' && profileIds.includes(storedActiveId)
            ? storedActiveId
            : null;

        const firstActive =
          profiles.find((p: any) => p.prId > 0 && p.prId != 7)?.prId ?? null;

        this.setActiveProfileId(validStored ?? firstActive ?? profileIds[0] ?? null);

      }
    } catch (error) {
      console.error("Error obteniendo usuario por email:", error);
    }
  }

  /** Controla la frecuencia de ejecución */
  private setCounter(): boolean {
    this.callCount++;
    return this.callCount === 1 || this.callCount % this.triggerFrequency === 0;
  }

  /** Parsea JSON de forma segura */
  private safeParse(value: string | null): any {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  // Profiles
  watchProfileIds(): Observable<number[]> {
    return this.profileIds$.asObservable();
  }

  // escuchar perfil activo
  watchActiveProfileId(): Observable<number | null> {
    return this.activeProfileId$.asObservable();
  }

  // getters sync (opcionales)
  get profileIds(): number[] {
    return this.profileIds$.value;
  }

  get activeProfileId(): number | null {
    return this.activeProfileId$.value;
  }

  // cambiar perfil activo (desde el header)
  setActiveProfileId(prId: number | null): void {
    if (prId !== null && !this.profileIds$.value.includes(prId)) return;

    this.activeProfileId$.next(prId);
    localStorage.setItem(this.LS_ACTIVE_PROFILE_ID, JSON.stringify(prId));
  }

}
